import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../shared/services/apiClient';
import { readSavedMulti, saveMulti, setActiveBusinessIds, activeBusinessIds, subscribeBusinessIds, installBusinessIds } from '../utils/businessMulti';

installBusinessIds(apiClient);

// Re-render key for pages: changes whenever the multi-selection changes.
export function useBusinessMultiKey() {
  return useSyncExternalStore(subscribeBusinessIds, () => activeBusinessIds().join(','));
}

// Sidebar switcher. Clicking a name selects only that business (or All);
// '+' adds a business to the current view and '−' removes one (the last
// one is never removed). 0 picked = All, 1 = single view, 2+ = a subset.
export default function BusinessMultiSelect({ userId, locations, selectedBusiness, setSelectedBusiness, labelFor }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const valid = new Set(locations.map((b) => Number(b.id)));
  const multi = activeBusinessIds();
  const picked = multi.length ? multi : (selectedBusiness ? [Number(selectedBusiness)] : []);

  useEffect(() => {
    if (!locations.length) return;
    const saved = readSavedMulti(userId).filter((id) => valid.has(id));
    if (saved.length >= 2) { setActiveBusinessIds(saved); if (selectedBusiness) setSelectedBusiness(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, locations.length]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const apply = (ids) => {
    const next = ids.filter((id) => valid.has(id));
    saveMulti(userId, next.length >= 2 ? next : []);
    setActiveBusinessIds(next);
    setSelectedBusiness(next.length === 1 ? next[0] : '');
    queryClient.invalidateQueries();
  };
  const only = (ids) => { apply(ids); setOpen(false); };
  const add = (id) => apply([...picked, id]);
  const remove = (id) => { if (picked.length > 1) apply(picked.filter((x) => x !== id)); };
  const summary = picked.length === 0 ? 'All Businesses'
    : picked.length === 1 ? labelFor(locations.find((b) => Number(b.id) === picked[0]) || { name: 'Business' })
      : `${picked.length} businesses`;

  return (
    <div ref={box} className="relative">
      <div className="flex items-center gap-1">
        <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 bg-gray-800 text-white rounded px-3 min-h-[44px] text-sm text-left truncate">{summary}</button>
          {picked.length > 0 && (
          <button type="button" onClick={() => only([])} aria-label="Clear selection" title="Clear selection"
            className="w-11 h-11 shrink-0 text-xl leading-none rounded bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white">×</button>
        )}
      </div>
      {open && (
        <div role="listbox" className="absolute z-20 mt-1 w-full max-h-80 overflow-auto bg-gray-800 text-white rounded shadow-lg py-1 text-sm">
          <button type="button" onClick={() => only([])}
            className={`w-full text-left px-3 min-h-[44px] ${picked.length === 0 ? 'bg-gray-700 font-semibold' : 'hover:bg-gray-700'}`}>All Businesses</button>
          {locations.map((b) => {
            const id = Number(b.id);
            const on = picked.includes(id);
            return (
              <div key={b.id} className={`flex items-center ${on ? 'bg-gray-700' : ''}`}>
                <button type="button" onClick={() => only([id])} aria-selected={on}
                  className={`flex-1 min-w-0 truncate text-left px-3 min-h-[44px] hover:bg-gray-600 ${on ? 'font-semibold' : ''}`}>{labelFor(b)}</button>
                {on ? (
                  <button type="button" onClick={() => remove(id)} disabled={picked.length <= 1}
                    aria-label={`Remove ${b.name} from the view`} title="Remove from view"
                    className="w-11 h-11 shrink-0 text-lg leading-none rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed">−</button>
                ) : (
                  <button type="button" onClick={() => add(id)}
                    aria-label={`Add ${b.name} to the view`} title="Add to view"
                    className="w-11 h-11 shrink-0 text-lg leading-none rounded hover:bg-gray-600">+</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
